import { useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  alpha,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CustomInput from "../../FormFields/CustomInput/CustomInput";
import CustomDate from "../../CustomForm/CustomDate";
import CustomCheckbox from "../../CustomForm/CustomCheckbox";
import CustomAutoComplete from "../../FormFields/CustomSelect/CustomAutoComplete";
import { QK } from "../../../base/qk";
import { useQuery } from "react-query";
import { searchCandidates } from "../../../api/services/candidate";

const usePersonSearch = (searchTerm) =>
  useQuery(
    [QK.searchCareers, searchTerm],
    () => searchCandidates(searchTerm),
    { enabled: true, keepPreviousData: true }
  );

const RiskNotification = ({ formData, handleChange }) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const [managerSearch, setManagerSearch] = useState("");
  const [reporterSearch, setReporterSearch] = useState("");
  const [completedBySearch, setCompletedBySearch] = useState("");

  const managerQuery = usePersonSearch(managerSearch);
  const reporterQuery = usePersonSearch(reporterSearch);
  const completedByQuery = usePersonSearch(completedBySearch);

  const formatOptions = (data) =>
    (data || []).map((person) => ({
      id: person.id,
      name: `${person.first_name} ${person.last_name}`.trim(),
    }));

  const updateRiskData = (updates) => {
    handleChange({
      target: {
        name: "risk_notification",
        value: { ...formData.risk_notification, ...updates },
      },
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    updateRiskData({ [name]: type === "checkbox" ? checked : value });
  };

  const handlePersonSelect = (field) => (selectedId) => {
    updateRiskData({ [field]: selectedId });
  };

  const handleSearchInput = (setter) => (value) => {
    setter(value);
  };

  const isNotRequired = formData.risk_notification?.not_required || false;

  return (
    <Accordion
      expanded={expanded}
      onChange={(e, isExpanded) => setExpanded(isExpanded)}
      sx={{
        boxShadow: "none",
        border: "1px solid #EAECF0",
        borderRadius: "12px !important",
        overflow: "hidden",
        "&:before": { display: "none" },
        "&.Mui-expanded": { margin: 0 },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: "#475467" }} />}
        sx={{
          backgroundColor: "#FAFBFC",
          minHeight: "56px",
          "&.Mui-expanded": { minHeight: "56px" },
          "& .MuiAccordionSummary-content": {
            margin: "12px 0",
            "&.Mui-expanded": { margin: "12px 0" },
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            width: "100%",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <WarningAmberIcon
                sx={{ fontSize: 18, color: theme.palette.primary.main }}
              />
            </Box>
            <Box>
              <Typography
                variant="h6"
                fontWeight={700}
                color="#1A1A2E"
                fontSize="0.9rem"
              >
                Risk Notification
              </Typography>
              <Typography variant="caption" color="#6B7280" fontSize="0.7rem">
                Internal risk escalation and follow-up
              </Typography>
            </Box>
          </Box>

          <Box onClick={(e) => e.stopPropagation()}>
            <CustomCheckbox
              name="not_required"
              label="Not required"
              isInitiallyChecked={isNotRequired}
              onClick={(checked) =>
                updateRiskData({ not_required: checked })
              }
            />
          </Box>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ p: 3, bgcolor: "#FFFFFF" }}>
        {isNotRequired && (
          <Box
            sx={{
              p: 2,
              borderRadius: "10px",
              bgcolor: "#F9FAFB",
              border: "1px solid #EAECF0",
              mb: 2,
            }}
          >
            <Typography
              variant="body2"
              color="#6B7280"
              fontSize="0.8rem"
              fontWeight={500}
            >
              Risk notification marked as <strong>Not Required</strong> for
              this incident.
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 2,
            opacity: isNotRequired ? 0.5 : 1,
            pointerEvents: isNotRequired ? "none" : "auto",
          }}
        >
          <CustomInput
            name="reference_number"
            label="Risk Reference Number"
            value={formData.risk_notification?.reference_number || ""}
            onChange={handleInputChange}
          />

          <CustomAutoComplete
            options={formatOptions(managerQuery.data)}
            isLoading={managerQuery.isLoading}
            value={formData?.risk_notification?.manager_informed || null}
            onChange={handlePersonSelect("manager_informed")}
            inputValue={managerSearch}
            onInputChange={handleSearchInput(setManagerSearch)}
            label="Name of Manager Informed"
            placeholder="Search for a manager..."
          />

          <CustomInput
            name="other_manager"
            label="Other Manager Informed"
            value={formData.risk_notification?.other_manager || ""}
            onChange={handleInputChange}
          />

          <CustomAutoComplete
            options={formatOptions(reporterQuery.data)}
            isLoading={reporterQuery.isLoading}
            value={formData?.risk_notification?.reported_by || null}
            onChange={handlePersonSelect("reported_by")}
            inputValue={reporterSearch}
            onInputChange={handleSearchInput(setReporterSearch)}
            label="Reported By"
            placeholder="Search for a reporter..."
          />

          <CustomInput
            name="other_reporter"
            label="Other Reporter"
            value={formData.risk_notification?.other_reporter || ""}
            onChange={handleInputChange}
          />

          <CustomAutoComplete
            options={formatOptions(completedByQuery.data)}
            isLoading={completedByQuery.isLoading}
            value={formData?.risk_notification?.completed_by || null}
            onChange={handlePersonSelect("completed_by")}
            inputValue={completedBySearch}
            onInputChange={handleSearchInput(setCompletedBySearch)}
            label="Completed By"
            placeholder="Search for a person..."
          />

          <CustomInput
            name="other_completed_by"
            label="Others"
            value={formData.risk_notification?.other_completed_by || ""}
            onChange={handleInputChange}
          />

          <CustomDate
            name="date_informed"
            label="Date Informed"
            value={formData.risk_notification?.date_informed || ""}
            onChange={handleInputChange}
          />

          <CustomDate
            name="sent_on"
            label="Sent On"
            value={formData.risk_notification?.sent_on || ""}
            onChange={handleInputChange}
          />
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default RiskNotification;